import { Ability } from '@casl/ability';
import { Actions, ResourceBulkForm, Subjects } from '@dispatch/shared/types';
import { CheckBoxField, DateField, Section, TextField } from '@dispatch/web/ui';
import { Grid } from '@mui/material';
import { useEffect } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

export type ServiceInformationProps = {
  control: Control<FieldValues>;
  index: number;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  watch: UseFormWatch<ResourceBulkForm>;
  resourceSetValue: UseFormSetValue<ResourceBulkForm>;
  trigger?: UseFormTrigger<ResourceBulkForm>;
};

export const ServiceInformationBulk = ({
  control,
  index: locationIndex,
  permissions,
  errors,
  watch,
  resourceSetValue,
  trigger,
}: ServiceInformationProps) => {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const isVirtualLocation = watch(
    `locations.${locationIndex}.locationInformation.isVirtualLocation`
  );

  useEffect(() => {
    resourceSetValue(
      `locations.${locationIndex}.serviceInformation.isVirtualLocation`,
      isVirtualLocation
    );
  }, [isVirtualLocation, locationIndex, resourceSetValue]);

  const renderServiceDays = (days: string[]) =>
    days.map((day: string, index: number) => {
      return (
        <Grid item xs={6} key={day + String(index)}>
          <CheckBoxField
            control={control}
            id={day.toLowerCase()}
            name={`locations.${locationIndex}.serviceInformation.${day.toLowerCase()}`}
            label={day}
            data-testid={day}
            disabled={!hasUpsertPermissions}
            onChange={() => {
              trigger?.('locations');
            }}
          />
        </Grid>
      );
    });

  return (
    <Section title="Service Information">
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DateField
            id={`startDate`}
            name={`locations.${locationIndex}.serviceInformation.startDate`}
            label="Start Day"
            control={control}
            readOnly={!hasUpsertPermissions}
            required
            errors={
              errors && errors['serviceInformation']
                ? errors['serviceInformation']
                : null
            }
            onChange={() => {
              trigger?.('locations');
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <DateField
            id={`endDate`}
            name={`locations.${locationIndex}.serviceInformation.endDate`}
            label="End Day"
            control={control}
            readOnly={!hasUpsertPermissions}
            errors={
              errors?.['serviceInformation']
                ? errors['serviceInformation']
                : null
            }
            onChange={() => {
              trigger?.('locations');
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {!JSON.parse(isVirtualLocation ?? 'true') && (
            <TextField
              id={`allowedTravelRadius`}
              name={`locations.${locationIndex}.serviceInformation.allowedTravelRadius`}
              label="Allowable Travel Radius (miles)"
              type="number"
              control={control}
              inputProps={{
                readOnly: !hasUpsertPermissions,
              }}
              errors={
                errors && errors['serviceInformation']
                  ? errors['serviceInformation']
                  : null
              }
            />
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {renderServiceDays([
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ])}
      </Grid>
    </Section>
  );
};
